import moment from 'moment';
import type { TimeframeSelection } from '../../../types/timeframe';
import {
  formatWeeklyLabelCompact,
  formatWeeklyLabelFull,
} from './formatWeeklyLabel';

export interface Period {
  /** Stable unique key (ISO date of period start). */
  key: string;
  /** Human-readable column/category label (used in tables). */
  label: string;
  /** Full-length label shown on the chart x-axis (e.g. "August", "Aug 1, 2026", "Q3 2026"). */
  axisLabel: string;
  /** ISO date (YYYY-MM-DD) representing the start of the period. */
  date: string;
}

// Safety cap so an extreme daily range cannot generate thousands of columns.
const MAX_PERIODS = 372;

/**
 * Expands a committed TimeframeSelection into the ordered list of periods
 * (columns / chart categories) that the dashboard should render.
 *
 * The number and labelling of periods is derived purely from the selected
 * calendar date range + granularity, so changing the date range in the
 * calendar produces a different set of periods — and therefore re-renders
 * every chart, table and scorecard that is built from them.
 */
export function generatePeriods(timeframe: TimeframeSelection): Period[] {
  const { granularity, startDate, endDate } = timeframe;
  const start = moment(startDate);
  const end = moment(endDate);

  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return [];
  }

  if (granularity === 'entireRange') {
    return [
      {
        key: start.format('YYYY-MM-DD'),
        label: `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`,
        axisLabel: `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`,
        date: start.format('YYYY-MM-DD'),
      },
    ];
  }

  const unit: moment.unitOfTime.DurationConstructor =
    granularity === 'weekly'
      ? 'week'
      : granularity === 'daily'
      ? 'day'
      : granularity === 'quarterly'
      ? 'quarter'
      : 'month';

  const multiYear = start.year() !== end.year();
  const cursor = start.clone().startOf(unit as moment.unitOfTime.StartOf);
  const last = end.clone().endOf(unit as moment.unitOfTime.StartOf);
  const periods: Period[] = [];

  while (cursor.isSameOrBefore(last) && periods.length < MAX_PERIODS) {
    let label: string;
    let axisLabel: string;
    switch (unit) {
      case 'day':
        label = cursor.format(multiYear ? 'MMM D, YY' : 'MMM D');
        axisLabel = cursor.format('MMM D, YYYY');
        break;
      case 'week': {
        const weekStart = cursor.format('YYYY-MM-DD');
        label = formatWeeklyLabelCompact(weekStart, timeframe.startDate, timeframe.endDate);
        axisLabel = formatWeeklyLabelFull(weekStart, timeframe.startDate, timeframe.endDate);
        break;
      }
      case 'quarter':
        label = `${cursor.format('YYYY')} Q${cursor.quarter()}`;
        axisLabel = `Q${cursor.quarter()} ${cursor.format('YYYY')}`;
        break;
      case 'month':
      default:
        label = cursor.format(multiYear ? 'MMM YY' : 'MMM');
        axisLabel = cursor.format('MMMM');
        break;
    }

    periods.push({
      key: cursor.format('YYYY-MM-DD'),
      label,
      axisLabel,
      date: cursor.format('YYYY-MM-DD'),
    });

    cursor.add(1, unit);
  }

  return periods;
}
